import React, { FC, memo, Profiler, useMemo } from "react";
import { useAppSelector } from "../../../../app";
import AnnotationItem from "./AnnotationItem";
import "./AnnotationLayer.scss";
import { onRender } from "../../../../app/utils/profiling";
import annotationsForPageSelector from "../../../../annotation/selectors/annotationsForPageSelector";
import { shallowEqual } from "react-redux";
import useAnnotationHighlighting from "../../../hooks/useAnnotationHighlighting";

type Props = {
  page: number;
  pdfWidth: number;
  pdfHeight: number;
};

const AnnotationLayer: FC<Props> = ({ page, pdfWidth, pdfHeight }) => {
  // Manage highlighting via CSS classes (no React re-renders)
  useAnnotationHighlighting();

  // Memoize selector params to prevent unnecessary recalculations
  const selectorParams = useMemo(() => ({ page }), [page]);

  const annotations = useAppSelector(
    (state) => annotationsForPageSelector(state, selectorParams),
    shallowEqual,
  );

  // Later siblings paint on top, so an annotation nested inside another's
  // bounding rectangle stays visible and reachable instead of being covered.
  const annotationsLargestFirst = useMemo(
    () =>
      [...annotations].sort(
        (a, b) =>
          (b.pageTokenIndices?.length ?? 0) - (a.pageTokenIndices?.length ?? 0),
      ),
    [annotations],
  );

  return (
    <Profiler id="AnnotationLayer" onRender={onRender}>
      <div
        className="annotation-layer__page-container"
        style={{ width: `${pdfWidth}px`, height: `${pdfHeight}px` }}
      >
        {annotationsLargestFirst.map((annotation) => (
          <AnnotationItem
            key={annotation.id}
            page={page}
            annotation={annotation}
          />
        ))}
      </div>
    </Profiler>
  );
};

// Custom comparison function to prevent unnecessary re-renders
const arePropsEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.page === nextProps.page &&
    prevProps.pdfWidth === nextProps.pdfWidth &&
    prevProps.pdfHeight === nextProps.pdfHeight
  );
};

export default memo(AnnotationLayer, arePropsEqual);
